// src/pages/cms/faq/FaqCategoriesPage.jsx
import React from "react";
import axios from "axios";

export const base_url = process.env.REACT_APP_BASE_URL;
export const api_type  = process.env.REACT_APP_API_TYPE;

export default function FaqCategoriesPage() {
  // -------- shared --------
  const authHeader = React.useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }),
    []
  );

  // -------- view state --------
  const [mode, setMode] = React.useState("categories"); // 'categories' | 'faqs'
  const [selectedCategory, setSelectedCategory] = React.useState(null); // { id, title }

  // ====== Categories state ======
  const [catRows, setCatRows] = React.useState([]); // [{ id, title }]
  const [catLoading, setCatLoading] = React.useState(false);
  const [catError, setCatError] = React.useState("");

  // Category Add/Edit modal
  const [openModal, setOpenModal] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null); // null => Add
  const [title, setTitle] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [modalError, setModalError] = React.useState("");

  // ---- confirmation modal (for deleting category or faq) ----
  const [confirm, setConfirm] = React.useState({
    open: false,
    type: null,     // 'category' | 'faq'
    id: null,
    label: "",      // title (category) or question (faq)
    loading: false,
    error: "",
  });

  const openConfirmDelete = (type, id, label) => {
    setConfirm({ open: true, type, id, label, loading: false, error: "" });
  };
  const closeConfirm = () => {
    if (confirm.loading) return;
    setConfirm({ open: false, type: null, id: null, label: "", loading: false, error: "" });
  };

  const loadCategories = React.useCallback(async () => {
    setCatLoading(true); setCatError("");
    try {
      const { data } = await axios.get(
        `${base_url}/${api_type}/cms/partner/faqsCategory`,
        { headers: authHeader }
      );
      const items = data?.data?.list  ?? [];
      setCatRows(Array.isArray(items) ? items : []);
    } catch (e) {
      setCatError(e?.response?.data?.message || e?.message || "Failed to load categories");
    } finally {
      setCatLoading(false);
    }
  }, [authHeader]);

  React.useEffect(() => { loadCategories(); }, [loadCategories]);

  const openAddCategory = () => {
    setEditingId(null);
    setTitle("");
    setModalError("");
    setOpenModal(true);
  };

  const openEditCategory = (row) => {
    setEditingId(row?.id);
    setTitle(row?.title || "");
    setModalError("");
    setOpenModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setOpenModal(false);
    setEditingId(null);
    setTitle("");
    setModalError("");
  };

  const saveCategory = async () => {
    if (!title.trim()) {
      setModalError("Please enter a title.");
      return;
    }
    setSaving(true); setModalError("");
    try {
      const payload = { title: title.trim() };
      if (editingId) {
        await axios.put(
          `${base_url}/${api_type}/cms/partner/faqsCategory/${editingId}`,
          payload,
          { headers: authHeader }
        );
      } else {
        await axios.post(
          `${base_url}/${api_type}/cms/partner/faqsCategory`,
          payload,
          { headers: authHeader }
        );
      }
      setOpenModal(false);
      await loadCategories();
    } catch (e) {
      setModalError(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ====== FAQs (by selected category) ======
  const [faqRows, setFaqRows] = React.useState([]); // [{ id, question, answer, status, isStared }]
  const [faqLoading, setFaqLoading] = React.useState(false);
  const [faqError, setFaqError] = React.useState("");

  const loadFaqs = React.useCallback(async (categoryId) => {
    setFaqLoading(true); setFaqError("");
    try {
      const { data } = await axios.get(
        `${base_url}/${api_type}/cms/partner/faqsCategory/${categoryId}/faqs`,
        { headers: authHeader }
      );
      const items = data?.data?.list  ?? [];
      setFaqRows(Array.isArray(items) ? items : []);
    } catch (e) {
      setFaqError(e?.response?.data?.message || e?.message || "Failed to load FAQs");
    } finally {
      setFaqLoading(false);
    }
  }, [authHeader]);

  const onViewCategory = (row) => {
    setSelectedCategory({ id: row.id, title: row.title });
    setMode("faqs");
    loadFaqs(row.id);
  };

  const backToCategories = () => {
    setMode("categories");
    setSelectedCategory(null);
    setFaqRows([]);
    setFaqError("");
  };

  // FAQ Add/Edit modal state
  const [faqModalOpen, setFaqModalOpen] = React.useState(false);
  const [faqSaving, setFaqSaving] = React.useState(false);
  const [faqModalError, setFaqModalError] = React.useState("");
  const [editingFaqId, setEditingFaqId] = React.useState(null); // null => add

  const [faqQuestion, setFaqQuestion] = React.useState("");
  const [faqAnswer, setFaqAnswer] = React.useState("");
  const [faqStatus, setFaqStatus] = React.useState(true);
  const [faqIsStared, setFaqIsStared] = React.useState(false);

  const openAddFaq = () => {
    setEditingFaqId(null);
    setFaqQuestion("");
    setFaqAnswer("");
    setFaqStatus(true);
    setFaqIsStared(false);
    setFaqModalError("");
    setFaqModalOpen(true);
  };

  const openEditFaq = (faq) => {
    setEditingFaqId(faq.id);
    setFaqQuestion(faq.question || "");
    setFaqAnswer(faq.answer || "");
    setFaqStatus(!!faq.status);
    setFaqIsStared(!!faq.isStared);
    setFaqModalError("");
    setFaqModalOpen(true);
  };

  const closeFaqModal = () => {
    if (faqSaving) return;
    setFaqModalOpen(false);
    setEditingFaqId(null);
    setFaqQuestion("");
    setFaqAnswer("");
    setFaqStatus(true);
    setFaqIsStared(false);
    setFaqModalError("");
  };

  const saveFaq = async () => {
    if (!faqQuestion.trim()) {
      setFaqModalError("Please enter a question.");
      return;
    }
    if (!faqAnswer.trim()) {
      setFaqModalError("Please enter an answer.");
      return;
    }
    setFaqSaving(true); setFaqModalError("");
    try {
      const payload = {
        question: faqQuestion.trim(),
        answer: faqAnswer.trim(),
        status: !!faqStatus,
        isStared: !!faqIsStared,
      };

      if (editingFaqId) {
        // Update existing FAQ
        await axios.put(
          `${base_url}/${api_type}/cms/partner/faqsCategory/${selectedCategory.id}/faqs/${editingFaqId}`,
          payload,
          { headers: authHeader }
        );
      } else {
        // Create new FAQ under selected category
        await axios.post(
          `${base_url}/${api_type}/cms/partner/faqsCategory/${selectedCategory.id}/faqs`,
          payload,
          { headers: authHeader }
        );
      }
      setFaqModalOpen(false);
      await loadFaqs(selectedCategory.id);
    } catch (e) {
      setFaqModalError(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setFaqSaving(false);
    }
  };

  // ---- delete actions use the confirmation modal ----
  const doConfirmDelete = async () => {
    if (!confirm.open || !confirm.type || !confirm.id) return;
    setConfirm((c) => ({ ...c, loading: true, error: "" }));
    try {
      if (confirm.type === "category") {
        await axios.delete(
          `${base_url}/${api_type}/cms/partner/faqsCategory/${confirm.id}`,
          { headers: authHeader }
        );
        // If deleting the one being viewed (defensive)
        if (mode === "faqs" && selectedCategory?.id === confirm.id) {
          setMode("categories");
          setSelectedCategory(null);
        }
        await loadCategories();
      } else if (confirm.type === "faq") {
        await axios.delete(
          `${base_url}/${api_type}/cms/partner/faqsCategory/${selectedCategory.id}/faqs/${confirm.id}`,
          { headers: authHeader }
        );
        if (selectedCategory?.id) {
          await loadFaqs(selectedCategory.id);
        }
      }
      closeConfirm();
    } catch (e) {
      setConfirm((c) => ({
        ...c,
        loading: false,
        error: e?.response?.data?.message || e?.message || "Delete failed",
      }));
    }
  };

  // ====== UI ======
  return (
    <div>
      {mode === "categories" ? (
        <>
          <div className="bg-white p-4 rounded-lg mb-4 shadow-md">
            <div className="flex items-center justify-between">
              <h1 className="text-[#252C32] text-[24px] font-bold">FAQ Categories</h1>
              <button
                onClick={openAddCategory}
                className="bg-[#ffb819] px-4 py-2 rounded-lg font-montserrat"
              >
                Add Category
              </button>
            </div>
          </div>

          <div className="overflow-x-auto w-full">
            {catError && (
              <div className="mx-5 my-2 px-3 py-2 rounded bg-red-50 text-red-700 border border-red-200">
                {catError}
              </div>
            )}

            {catLoading ? (
              <div className="p-5 text-sm text-gray-600">Loading…</div>
            ) : (
              <div className="overflow-hidden border rounded-2xl shadow-sm bg-white">
                <div className="px-4 py-3 border-b bg-gray-50/60">
                  <h2 className="text-base font-semibold text-gray-900">Categories</h2>
                </div>

                <table className="min-w-full table-fixed">
                  <colgroup>
                    <col style={{ width: "65%" }} />
                    <col style={{ width: "35%" }} />
                  </colgroup>
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="text-left p-3 border-b">Title</th>
                      <th className="text-center p-3 border-b">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catRows.length ? catRows.map((r) => (
                      <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                        <td className="p-3 border-b align-top">
                          <div className="text-gray-900">{r.title || "—"}</div>
                        </td>
                        <td className="p-3 border-b align-top">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              className="px-3 py-1.5 rounded border hover:bg-gray-50"
                              onClick={() => onViewCategory(r)}
                            >
                              View
                            </button>
                            <button
                              className="px-3 py-1.5 rounded border hover:bg-gray-50"
                              onClick={() => openEditCategory(r)}
                            >
                              Edit
                            </button>
                            <button
                              className="px-3 py-1.5 rounded border text-red-600 hover:bg-red-50"
                              onClick={() => openConfirmDelete("category", r.id, r.title)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td className="p-4 text-sm text-gray-500" colSpan={2}>
                          No categories found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Modal: Add/Edit Category */}
          {openModal && (
            <div className="fixed inset-0 z-[1000] flex items-center justify-center">
              <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
              <div className="relative bg-white w-[96%] max-w-md rounded-xl shadow-2xl overflow-hidden z-[1001]">
                <div className="px-6 py-4 border-b">
                  <h2 className="text-lg font-semibold">
                    {editingId ? "Edit Category" : "Add Category"}
                  </h2>
                </div>
                <div className="px-6 py-5 space-y-4">
                  {modalError && (
                    <div className="px-3 py-2 rounded bg-red-50 text-red-700 border border-red-200">
                      {modalError}
                    </div>
                  )}
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">Title</span>
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter category title"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
                    />
                  </label>
                </div>
                <div className="px-6 py-4 border-t flex items-center justify-between">
                  <button onClick={closeModal} className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-50" disabled={saving}>
                    Cancel
                  </button>
                  <button
                    onClick={saveCategory}
                    disabled={saving || !title.trim()}
                    className="px-5 py-2 rounded-lg bg-[#1a73e8] text-white hover:opacity-95 disabled:opacity-60"
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        // ===== FAQs view (same page, conditional render) =====
        <>
          <div className="bg-white p-4 rounded-lg mb-4 shadow-md">
            <div className="flex items-center justify-end">
              <div className="flex items-center gap-2">
                <button
                  onClick={openAddFaq}
                  className="px-4 py-2 rounded-lg bg-[#ffb819] text-black"
                >
                  Add FAQ
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto w-full">
            {faqError && (
              <div className="mx-5 my-2 px-3 py-2 rounded bg-red-50 text-red-700 border border-red-200">
                {faqError}
              </div>
            )}
            {faqLoading ? (
              <div className="p-5 text-sm text-gray-600">Loading…</div>
            ) : (
              <div className="overflow-hidden border rounded-2xl shadow-sm bg-white">
                <div className="px-4 py-3 border-b bg-gray-50/60">
                  <h2 className="text-base font-semibold text-gray-900">FAQs</h2>
                </div>

                <table className="min-w-full table-fixed">
                  <colgroup>
                    <col style={{ width: "28%" }} />
                    <col style={{ width: "28%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "16%" }} />
                  </colgroup>
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="text-left p-3 border-b">Question</th>
                      <th className="text-left p-3 border-b">Answer</th>
                      <th className="text-center p-3 border-b">Status</th>
                      <th className="text-center p-3 border-b">Starred</th>
                      <th className="text-center p-3 border-b">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {faqRows.length ? faqRows.map((r, idx) => (
                      <tr key={r.id ?? `${idx}-${r.question}`} className="odd:bg-white even:bg-gray-50">
                        <td className="p-3 border-b align-top">{r.question || "—"}</td>
                        <td className="p-3 border-b align-top">{r.answer || "—"}</td>
                        <td className="p-3 border-b align-top text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${r.status ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                            {r.status ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="p-3 border-b align-top text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${r.isStared ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-600"}`}>
                            {r.isStared ? "Starred" : "—"}
                          </span>
                        </td>
                        <td className="p-3 border-b align-top">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              className="px-3 py-1.5 rounded border hover:bg-gray-50"
                              onClick={() => openEditFaq(r)}
                            >
                              Edit
                            </button>
                            <button
                              className="px-3 py-1.5 rounded border text-red-600 hover:bg-red-50"
                              onClick={() => openConfirmDelete("faq", r.id, r.question)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td className="p-4 text-sm text-gray-500" colSpan={5}>
                          No FAQs yet for this category.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Modal: Add/Edit FAQ */}
          {faqModalOpen && (
            <div className="fixed inset-0 z-[1000] flex items-center justify-center">
              <div className="absolute inset-0 bg-black/40" onClick={closeFaqModal} />
              <div className="relative bg-white w-[96%] max-w-2xl rounded-xl shadow-2xl overflow-hidden z-[1001]">
                <div className="px-6 py-4 border-b">
                  <h2 className="text-lg font-semibold">
                    {editingFaqId ? "Edit FAQ" : "Add FAQ"}
                  </h2>
                </div>

                <div className="px-6 py-5 space-y-4">
                  {faqModalError && (
                    <div className="px-3 py-2 rounded bg-red-50 text-red-700 border border-red-200">
                      {faqModalError}
                    </div>
                  )}

                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">Question</span>
                    <input
                      value={faqQuestion}
                      onChange={(e) => setFaqQuestion(e.target.value)}
                      placeholder="Enter question"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
                    />
                  </label>

                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">Answer</span>
                    <textarea
                      value={faqAnswer}
                      onChange={(e) => setFaqAnswer(e.target.value)}
                      rows={4}
                      placeholder="Enter answer"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
                    />
                  </label>

                  <div className="flex items-center gap-6">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={faqStatus}
                        onChange={(e) => setFaqStatus(e.target.checked)}
                      />
                      <span>Status (Active)</span>
                    </label>

                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={faqIsStared}
                        onChange={(e) => setFaqIsStared(e.target.checked)}
                      />
                      <span>Starred</span>
                    </label>
                  </div>
                </div>

                <div className="px-6 py-4 border-t flex items-center justify-between">
                  <button
                    onClick={closeFaqModal}
                    className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-50"
                    disabled={faqSaving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveFaq}
                    disabled={faqSaving || !faqQuestion.trim() || !faqAnswer.trim()}
                    className="px-5 py-2 rounded-lg bg-[#1a73e8] text-white hover:opacity-95 disabled:opacity-60"
                  >
                    {faqSaving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ---------- Confirmation Modal (shared) ---------- */}
      {confirm.open && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeConfirm} />
          <div className="relative bg-white w-[96%] max-w-md rounded-xl shadow-2xl overflow-hidden z-[1101]">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Confirm Delete</h2>
            </div>

            <div className="px-6 py-5 space-y-3">
              {confirm.error && (
                <div className="px-3 py-2 rounded bg-red-50 text-red-700 border border-red-200">
                  {confirm.error}
                </div>
              )}
              <p className="text-sm text-gray-700">
                Are you sure you want to delete this{" "}
                <strong>{confirm.type === "category" ? "category" : "FAQ"}</strong>
                {confirm.label ? <>: <em>“{confirm.label}”</em></> : null}?
              </p>
              <p className="text-xs text-gray-500">
                This action cannot be undone.
              </p>
            </div>

            <div className="px-6 py-4 border-t flex items-center justify-between">
              <button
                onClick={closeConfirm}
                className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-50"
                disabled={confirm.loading}
              >
                Cancel
              </button>
              <button
                onClick={doConfirmDelete}
                disabled={confirm.loading}
                className="px-5 py-2 rounded-lg bg-red-600 text-white hover:opacity-95 disabled:opacity-60"
              >
                {confirm.loading ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
